import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  isNotificationSpeechEnabled,
  setNotificationSpeechEnabled,
  subscribeWorkModeChanged,
} from '../../utils/workModePrefs';
import { updateUserStatus } from '../../store/slices/authSlice';
import notificationService from '../../services/notificationService';

const SpeakerIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13 5.5v13a1 1 0 01-1.6.8L7.2 16H4a1 1 0 01-1-1V9a1 1 0 011-1h3.2l4.2-3.3A1 1 0 0113 5.5z" />
    <path d="M16.2 8.2a1 1 0 011.4 0 5.5 5.5 0 010 7.8 1 1 0 11-1.4-1.4 3.5 3.5 0 000-5 1 1 0 010-1.4z" />
    <path d="M18.8 5.6a1 1 0 011.4 0 9.5 9.5 0 010 13.4 1 1 0 11-1.4-1.4 7.5 7.5 0 000-10.6 1 1 0 010-1.4z" />
  </svg>
);

const TickMark = ({ done }) => (
  <span
    className={`ml-auto inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
      done ? 'bg-emerald-500 text-white' : 'border border-gray-300 dark:border-gray-600'
    }`}
    aria-hidden="true"
  >
    {done ? (
      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
      </svg>
    ) : null}
  </span>
);

const WorkModeSpeechControls = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [speak, setSpeak] = useState(() => isNotificationSpeechEnabled());
  const [showHelp, setShowHelp] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const loggedIn = Boolean(user?.id);
  const workFromHome = String(user?.workLocation || user?.status || '').toUpperCase() === 'WORK_FROM_HOME';
  const eligible = loggedIn && workFromHome;

  useEffect(() => subscribeWorkModeChanged(() => {
    setSpeak(isNotificationSpeechEnabled());
  }), []);

  useEffect(() => {
    setSpeak(isNotificationSpeechEnabled());
  }, [user?.id, user?.status, user?.workLocation]);

  const playSpeechSample = () => {
    notificationService.primeSpeech();
    notificationService.speakNotification(
      'Notification speech is on',
      'You will hear alerts while working from home.',
      { fromUserGesture: true }
    );
  };

  const stopSpeech = () => {
    setNotificationSpeechEnabled(false);
    setSpeak(false);
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    try { window.workloopRealm?.stopSpeaking?.(); } catch { /* ignore */ }
  };

  const handleIconClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (speak) {
      stopSpeech();
      return;
    }
    setShowHelp(true);
  };

  const toggleSpeak = (event) => {
    event.stopPropagation();
    if (!eligible) {
      setNotificationSpeechEnabled(false);
      setSpeak(false);
      return;
    }
    const next = !isNotificationSpeechEnabled();
    setNotificationSpeechEnabled(next);
    setSpeak(next);
    if (next) {
      playSpeechSample();
    } else {
      window.speechSynthesis?.cancel();
    }
  };

  const setWorkFromHome = async (event) => {
    event.stopPropagation();
    if (!loggedIn || workFromHome || savingStatus) return;
    setSavingStatus(true);
    try {
      await dispatch(updateUserStatus({ workLocation: 'WORK_FROM_HOME', source: 'MANUAL' })).unwrap();
      setNotificationSpeechEnabled(true);
      setSpeak(true);
      playSpeechSample();
    } catch {
      // Keep popup open so the user can retry
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={speak}
        aria-label={speak ? 'Notification speech on' : 'Notification speech off'}
        title={speak ? 'Speech on' : 'Speech off'}
        onClick={handleIconClick}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          speak ? 'bg-blue-50 dark:bg-blue-500/20' : 'bg-gray-400'
        }`}
      >
        <SpeakerIcon className={`h-5 w-5 ${speak ? 'text-blue-500' : 'text-white'}`} />
      </button>

      {showHelp && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40"
          onClick={(e) => {
            e.stopPropagation();
            setShowHelp(false);
          }}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-gray-900 dark:text-white">Notification audio</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Audio will work only when (default setting):
            </p>
            <ol className="mt-3 space-y-3 text-sm">
              <li className={`flex items-center gap-2 ${loggedIn ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-200'}`}>
                <span className="font-semibold">1.</span>
                <span className="font-medium">Logged in</span>
                <TickMark done={loggedIn} />
              </li>
              <li className={`flex flex-col gap-1 ${workFromHome ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">2.</span>
                  <span className="font-medium">Work from Home</span>
                  <TickMark done={workFromHome} />
                </div>
                {!workFromHome ? (
                  <button
                    type="button"
                    onClick={setWorkFromHome}
                    disabled={!loggedIn || savingStatus}
                    className="ml-6 text-left text-sm font-semibold text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
                  >
                    {savingStatus ? 'Updating…' : 'Click here to update Work from home'}
                  </button>
                ) : null}
              </li>
            </ol>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {speak
                ? 'Speech is on. New notifications will be read aloud.'
                : 'Speech stays off until you are logged in and working from home.'}
            </p>
            {eligible ? (
              <button
                type="button"
                onClick={(event) => {
                  const turningOff = isNotificationSpeechEnabled();
                  toggleSpeak(event);
                  if (turningOff) {
                    setShowHelp(false);
                  }
                }}
                className={`mt-4 w-full rounded-lg px-3 py-2 text-sm font-semibold ${
                  speak
                    ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {speak ? 'Turn speech off' : 'Turn speech on'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowHelp(false);
              }}
              className={`w-full rounded-lg px-3 py-2 text-sm font-semibold ${
                eligible
                  ? 'mt-2 bg-transparent text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  : 'mt-4 bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              Got it
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default WorkModeSpeechControls;
